import type { MessageKey } from './es';

/**
 * English catalogue.
 *
 * Typed against the Spanish one: a missing key is a compile error, not a
 * `undefined` on someone's screen. A test also checks the reverse direction —
 * TypeScript alone would not catch a key added here and forgotten there.
 */
export const en: Record<MessageKey, string> = {
  'common.appName': '29100 Cloud',
  'common.tagline': 'Kept for 29100.',
  'common.loading': 'Loading…',
  'common.retry': 'Retry',
  'common.loadMore': 'Load more',
  'common.cancel': 'Cancel',
  'common.save': 'Save changes',
  'common.delete': 'Delete',
  'common.download': 'Download',
  'common.share': 'Share',
  'common.open': 'Open',
  'common.rename': 'Rename',
  'common.edit': 'Edit',
  'cleanup.title': 'What you can clean up',
  'cleanup.body':
    'What takes up space and you probably no longer need. This only suggests: deleting is your call, and it goes to the trash.',
  'cleanup.empty': 'Nothing to suggest.',
  'cleanup.emptyBody': 'No duplicates, no large unopened files. Your space is clean.',
  'cleanup.reclaimable': 'Space you would get back',
  'cleanup.reclaimableHint':
    'This is your quota. Duplicates share the stored file, so deleting the copy frees quota for you.',
  'cleanup.listHint': 'Largest first.',
  'cleanup.reason.duplicate': 'Duplicate',
  'cleanup.reason.stale': 'Unopened',
  'cleanup.reason.large': 'Large',
  'cleanup.copies': '{count, plural, one {1 identical copy} other {{count} identical copies}}',
  'cleanup.lastOpened': 'Last opened on {date}',
  'cleanup.moveToTrash': 'Move to trash',
  'cleanup.moved': '{name} is in the trash.',
  'cleanup.confirmTitle': 'Move to trash',
  'cleanup.confirmBody': '{name} will go to the trash. You can restore it from there.',
  'nav.cleanup': 'Cleanup',
  'reviews.setDate': 'Review date',
  'reviews.title': 'What needs reviewing',
  'reviews.body':
    'Review dates for your folders: policies expiring, contracts renewing, certificates lapsing. We warn you ahead of time.',
  'reviews.empty': 'No folder has a review date.',
  'reviews.emptyBody':
    'Set one from the folder. Your profession suggests the cadence; you change it.',
  'reviews.overdueCount': 'are past due and still unreviewed.',
  'reviews.overdueBy': '{days, plural, one {1 day overdue} other {{days} days overdue}}',
  'reviews.inDays': '{days, plural, =0 {Due today} one {In 1 day} other {In {days} days}}',
  'reviews.markDone': 'Mark reviewed',
  'reviews.completed': 'Reviewed. No next one.',
  'reviews.completedNext': 'Reviewed. Next one on {date}.',
  'reviews.saved': 'Review date saved.',
  'reviews.cleared': 'This folder is no longer reviewed.',
  'reviews.clear': 'Remove the date',
  'reviews.dialogTitle': 'Review of {name}',
  'reviews.dialogBody': 'When this folder is due for review, and how often it repeats.',
  'reviews.dueOn': 'Review date',
  'reviews.recurrence': 'Repeats',
  'reviews.recurrence.NONE': 'Once only',
  'reviews.recurrence.MONTHLY': 'Every month',
  'reviews.recurrence.QUARTERLY': 'Every quarter',
  'reviews.recurrence.SEMIANNUAL': 'Every six months',
  'reviews.recurrence.ANNUAL': 'Every year',
  'reviews.noticeDays': 'Warn this many days ahead',
  'reviews.note': 'What to review',
  'reviews.notePlaceholder': 'Renew the liability policy',
  'reviews.disclaimer':
    'This is a reminder, not a guarantee: nothing is blocked when a date passes. Always check deadlines you are bound by.',
  'nav.reviews': 'Reviews',
  'archive.menu': 'Permanent archive',
  'archive.dialogTitle': 'Permanent archive: {name}',
  'archive.dialogBody':
    'Keeps this folder and everything inside it in archive storage: a third cheaper per month, but every read is billed separately.',
  'archive.alreadyCold':
    'This folder is already marked as a permanent archive, and so are the ones inside it.',
  'archive.filesInside': 'Files inside',
  'archive.sizeInside': 'Taking up',
  'archive.alreadyMoved': 'Already moved',
  'archive.movedCount': '{moved} of {total}',
  'archive.savings': 'At best you save {amount} a year, if they are never opened.',
  'archive.breakeven': 'Past {reads} reads a year this costs more than normal storage.',
  'archive.zipWarning':
    'Careful: downloading the whole folder as a ZIP reads every file at once, and each one counts as a read.',
  'archive.disclaimer':
    'Nothing moves right now. Nightly maintenance moves whatever has gone untouched long enough; files stay just as available, and unmarking costs nothing.',
  'archive.mark': 'Mark as archive',
  'archive.unmark': 'Back to normal',
  'archive.marked':
    '{folders, plural, one {The folder is now a permanent archive.} other {{folders} folders are now a permanent archive.}}',
  'archive.unmarked':
    '{folders, plural, one {The folder is back on normal storage.} other {{folders} folders are back on normal storage.}}',
  'common.favorite': 'Favorite',
  'common.close': 'Close',
  'common.done': 'Done',
  'common.copy': 'Copy',
  'common.back': 'Back',
  'common.comingSoon': 'Coming soon',
  'common.language': 'Language',
  'common.error': 'Something went wrong',
  'common.confirm': 'Confirm',
  'common.current': 'Current',
  'common.dismiss': 'Dismiss',
  'common.actions': 'Actions',
  'common.create': 'Create',

  'nav.dashboard': 'Dashboard',
  'nav.files': 'My Files',
  'nav.filesShort': 'Files',
  'nav.photos': 'Photos',
  'nav.albums': 'Albums',
  'nav.checklists': 'Checklists',
  'nav.requests': 'Requests',
  'nav.sharedLinks': 'Shared Links',
  'nav.sharedLinksShort': 'Links',
  'nav.favorites': 'Favorites',
  'nav.trash': 'Trash',
  'nav.activity': 'Activity',
  'nav.settings': 'Settings',
  'nav.team': 'Team',
  'nav.sharedFolders': 'Shared Folders',
  'nav.sharedFoldersShort': 'Folders',
  'nav.reports': 'Reports',
  'nav.pricing': 'Pricing',
  'nav.legal': 'Legal',
  'nav.terms': 'Terms',
  'nav.privacy': 'Privacy',
  'nav.acceptableUse': 'Acceptable use',
  'nav.contact': 'Contact',
  'nav.signIn': 'Sign in',
  'nav.signOut': 'Sign out',
  'nav.getStarted': 'Get Started',
  'nav.search': 'Search files, folders…',
  'nav.searching': 'Searching…',
  'nav.noMatches': 'No matches.',
  'nav.notifications': 'Notifications',
  'nav.account': 'Account',
  'nav.platformAdmin': 'Platform admin',
  'nav.skipToContent': 'Skip to content',
  'nav.openMenu': 'Open menu',
  'nav.closeMenu': 'Close menu',
  'nav.sections': 'Sections',
  'nav.main': 'Main navigation',

  'nav.group.library': 'My content',
  'nav.group.libraryBody': 'Your photos, albums and the checklists you follow.',
  'nav.group.sharing': 'Shared',
  'nav.group.sharingBody': 'What you opened up to other people, and what they opened up to you.',
  'nav.group.organization': 'Organization',
  'nav.group.organizationBody': 'The team, its deadlines and what happened in this workspace.',
  'nav.group.space': 'Storage',
  'nav.group.spaceBody': 'What takes up room for no reason, and what you already deleted.',
  'nav.expandGroup': 'Show {section}',
  'nav.collapseGroup': 'Hide {section}',
  'nav.openSection': 'Open',

  'navHint.sharedFolders': 'Folders other people in the organization gave you access to.',
  'navHint.sharedLinks': 'The links you created, with their downloads and expiry.',
  'navHint.requests': 'Ask someone for documents even if they have no account.',
  'navHint.trash': 'Deleted files stay here until they are purged: you can restore them.',
  'navHint.cleanup': 'Duplicates, large files and what you have not opened in months.',

  'landing.heroBadge': 'Adapts to {count} professions',
  'landing.heroTitle': 'The cloud that already knows how your profession works.',
  'landing.heroSubtitle':
    'Tell us what you do and your space is born organised: the folders, the names and the checklists of your trade, set up before you upload the first file.',
  'landing.heroNote': 'Free plan forever. No card needed.',
  'landing.startFree': 'Start for free',

  'landing.profilesTitle': 'Your space does not start empty',
  'landing.profilesBody':
    'When you create the account we ask what it is for. That single answer changes the vocabulary, the folder structure and what the platform reminds you of: a law firm does not keep “files in folders”, it keeps documents in case records.',
  'landing.profilesMore': 'And {count} more profiles, across these categories:',

  'landing.doTitle': 'Three things your current cloud does not do',
  'landing.doIntakeTitle': 'Ask for documents from people with no account',
  'landing.doIntakeBody':
    'You send a link, they see exactly what you are asking for —one slot per document— and they hand it over without signing up for anything. You watch the progress as it arrives.',
  'landing.doChecklistTitle': 'It knows which papers are missing',
  'landing.doChecklistBody':
    'Every procedure in your profession comes with its checklist. What is missing is visible at a glance instead of on delivery day.',
  'landing.doExportTitle': 'You can leave whenever you want',
  'landing.doExportBody':
    'Download a complete inventory of your account and schedule its deletion yourself, from Settings. Without writing to support and without explaining yourself.',

  'landing.pricingTitle': 'Start free, pay only if it works for you',
  'landing.pricingBody':
    'The free plan does not expire and asks for no card. Paid plans are taken when the space runs short, and cancelled from inside the app.',
  'landing.pricingFrom': 'Paid plans from {price} a month',
  'landing.seePlans': 'See every plan',
  'landing.explorePlans': 'Explore Plans',
  'landing.goToDashboard': 'Go to your dashboard',
  'landing.openYourSpace': 'Open your space',
  'landing.startPersonal': 'Start Personal',
  'landing.exploreBusiness': 'Explore Business',
  'landing.personalTitle': '29100 Cloud Personal',
  'landing.personalBody':
    'For people and families who want their memories and documents in one safe place.',
  'landing.businessTitle': '29100 Cloud Business',
  'landing.businessBody':
    'For professionals and teams: shared folders, roles, permissions and audit trail.',
  'landing.pillarSecurity': 'Security',
  'landing.pillarSecurityBody': 'Private storage, short-lived signed links and protected sessions.',
  'landing.pillarSimplicity': 'Simplicity',
  'landing.pillarSimplicityBody': 'Upload, organise and share without reading a manual.',
  'landing.pillarControl': 'Control',
  'landing.pillarControlBody': 'You decide who sees what, for how long, and how many times.',

  'trust.title': 'What we do so you can trust us',
  'trust.encryptionTitle': 'Always an encrypted connection',
  'trust.encryptionBody': 'HTTPS enforced, with no exceptions and no way back.',
  'trust.mfaTitle': 'Two-step verification',
  'trust.mfaBody': 'With the authenticator app you already use.',
  'trust.privacyTitle': 'We never store your IP',
  'trust.privacyBody': 'Nor your card details: payment happens elsewhere.',
  'trust.exportTitle': 'Export everything and leave',
  'trust.exportBody': 'On your own, from Settings, without asking anyone.',
  'trust.infraNote': 'We run on Cloudflare and Neon, certified to ISO 27001 and SOC 2 Type II.',

  'contact.title': 'Contact',
  'contact.lead':
    'We are finalising the support channels for 29100 Cloud. As soon as they are live we will publish everything here: the support address, a contact form and our service hours.',
  'contact.nextTitle': 'What you will find here shortly',
  'contact.nextSupport': 'Support email',
  'contact.nextSupportBody':
    'An address handled by the team running the platform, with every request tracked.',
  'contact.nextForm': 'Contact form',
  'contact.nextFormBody':
    'To write to us without leaving the site, with the subject already routed to the right people.',
  'contact.nextHours': 'Service hours and response times',
  'contact.nextHoursBody':
    'When we are available and how long a reply takes, so you know what to expect.',
  'contact.statusTitle': 'In the meantime',
  'contact.statusBody':
    'The platform is in its final rollout phase and will be fully operational shortly. If you need to reach us before then, write to the address published by the service operator — your message will be answered all the same.',
  'contact.note': 'We will update this page as soon as the channels are available.',

  'terms.title': 'Terms of Service',
  'terms.badge': 'Draft',
  'terms.updated': 'Last updated: {date}',
  'terms.provisional':
    'Provisional version, pending legal review before the commercial launch. It describes exactly how the platform works today; the final wording will be published on this same page.',
  'terms.lead':
    'These terms govern the use of 29100 Cloud. By creating an account you accept them; if you disagree with any of them, do not use the service.',
  'terms.operatorTitle': 'Who provides the service',
  'terms.operatorBody':
    '29100 Cloud is a file storage and sharing service operated from Ecuador and offered in Ecuador and the rest of Latin America. The provider’s registered name, tax ID (RUC) and address will be published in this section before the commercial launch; until then, the channel to reach us is the one listed on the Contact page.',
  'terms.accountTitle': 'Your account',
  'terms.accountBody':
    'Using the service requires an account with a valid email address. You are responsible for keeping your password secret and for what happens under your session; you can enable two-step verification from Settings. You must be of legal age or have your guardian’s authorisation, and the details you provide when registering must be truthful.',
  'terms.plansTitle': 'Plans, quotas and the free tier',
  'terms.plansBody':
    'Every new account starts on the free plan of its line — Personal or Business — with no trial period and no charge. Each plan sets a storage quota, a maximum file size and a number of spaces; the details are on the Pricing page and in Settings → Subscription. Changing plan always starts with a request from you: you are never charged for an upgrade you did not ask for. If you downgrade and exceed the new quota, you have {quotaGraceDays} days to free up space before the excess is moved to the trash.',
  'terms.paymentTitle': 'Payment, renewal and non-payment',
  'terms.paymentBody':
    'Billing is handled manually: you receive a payment link from an external provider and the plan is activated once the payment is confirmed. We do not store card details — none pass through the platform. Expiry is announced in advance and, once the period ends, there are {expiryGraceDays} days of grace. If payment does not arrive, the space becomes read-only after {suspendDays} days, is no longer accessible after {blockDays}, and is permanently deleted after {deleteDays}. Every step is announced before it happens and affects the unpaid space only, not your account or your other spaces: export or download whatever you need before the lockout, because after it you can no longer get in.',
  'terms.withdrawalTitle': 'Right of withdrawal',
  'terms.withdrawalBody':
    'If you contract as an end consumer, you may exercise the right of return that Ecuador’s Consumer Protection Act grants to those who contract by electronic means, within the legal period and through the Contact page. Cancelling a paid plan never requires a call or an email: you do it from Settings, in the same steps in which you signed up.',
  'terms.contentTitle': 'Your files and your content',
  'terms.contentPromise':
    'The files you upload are yours. We do not use them for anything other than providing the service, we do not hand them to third parties, we do not analyse them for commercial or advertising purposes, and we do not train models on them.',
  'terms.contentBody':
    'You authorise us only to store, transmit and process them as far as the service requires: keeping them, showing you a preview, generating a thumbnail, zipping a folder you asked to download, or delivering them to whoever you choose to share with. You are responsible for holding the rights to what you upload. You can download your files and export your inventory at any time from Settings → Privacy.',
  'terms.useTitle': 'Acceptable use',
  'terms.useBody':
    'You may not use 29100 Cloud to store or distribute unlawful content, material you hold no rights to, or anything meant to compromise the security of the platform or of others. The details are on the Acceptable Use page. In the event of a serious breach or an order from a competent authority we may suspend the affected space; unless the law prevents it, we will tell you why.',
  'terms.sharingTitle': 'Sharing and public links',
  'terms.sharingBody':
    'A shared link is created and governed by you: you can protect it with a password, give it an expiry date and a maximum number of downloads, and revoke it whenever you want. While it is active, anyone holding the link can reach what you shared, so sharing is your decision and your responsibility. On Business you can also restrict a folder to specific people on your team.',
  'terms.availabilityTitle': 'Availability and backups',
  'terms.availabilityBody':
    'We work to keep the service continuously available, but we do not commit to an uptime percentage or a response time until a signed service level agreement exists. There may be interruptions for maintenance, because an infrastructure provider fails, or for reasons beyond our control. The trash keeps what you delete for the period set by your plan and you can restore it from there, but that is not a substitute for your own backup: keep one for anything critical.',
  'terms.dataTitle': 'Data protection',
  'terms.dataBody':
    'We process your personal data in accordance with Ecuador’s Personal Data Protection Act. What we store, why, and for how long is set out in the Privacy Policy. We do not keep IP addresses in the clear. Storage and database infrastructure is provided by international vendors, so your data may be hosted outside Ecuador with the safeguards the law requires. The rights of access, portability and erasure are exercised from Settings → Privacy, without writing to anyone.',
  'terms.terminationTitle': 'Cancellation, closure and deletion',
  'terms.terminationBody':
    'You can cancel your subscription whenever you want from Settings: cancelling does not close your account, it drops you to the free plan and you keep your access. Closing the account is also requested from Settings and takes effect after {accountDeletionDays} days; during that period the account works normally and you can call the closure off. When it expires, your files are deleted and your profile is anonymised. Billing records are kept with no link to you, as accounting rules require.',
  'terms.liabilityTitle': 'Liability',
  'terms.liabilityBody':
    'We provide the service with due care, but we are not liable for data loss caused by your own deletion, by the loss of your credentials, by what third parties do with a link you shared, or by the failure of an external infrastructure provider. Except in cases of wilful misconduct or gross negligence, and as far as the law allows it to be limited, our liability is capped at the amount you paid for the service in the twelve months before the event. None of this cuts back the rights the law grants to consumers.',
  'terms.changesTitle': 'Changes to these terms',
  'terms.changesBody':
    'We may update these terms. A significant change is announced with reasonable notice inside the application and is reflected in the update date on this page. If a change does not suit you, you can cancel or close your account before it takes effect.',
  'terms.lawTitle': 'Governing law',
  'terms.lawBody':
    'These terms are governed by the laws of the Republic of Ecuador and any dispute is submitted to its competent courts. If you contract as an end consumer from another Latin American country, this does not deprive you of the rights or the jurisdiction granted to you by the law of your place of residence.',
  'terms.contactNote': 'Questions about these terms? Write to us from the Contact page.',

  'auth.signInTitle': 'Sign in to 29100 Cloud',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.signingIn': 'Signing in…',
  'auth.noAccount': "Don't have an account?",
  'auth.createOne': 'Create one',
  'auth.forgotPassword': 'Forgot your password?',
  'auth.signInFailed': 'Could not sign in',
  'auth.captchaPending': 'Complete the security check to continue.',
  'auth.captchaFailed':
    'The security check could not load. Check your connection and reload the page.',
  'auth.sessionNotStored':
    'Your credentials are correct, but the browser did not keep the session. This usually means third-party cookies are blocked: try a normal (not private) window, or allow cookies for this site.',

  'dashboard.welcome': 'Welcome back, {name}',
  'dashboard.uploadFiles': 'Upload Files',
  'dashboard.newFolder': 'New Folder',
  'dashboard.createLink': 'Create Link',
  'dashboard.totalStorage': 'Total Storage',
  'dashboard.files': 'Files',
  'dashboard.sharedLinks': 'Shared Links',
  'dashboard.photos': 'Photos',
  'dashboard.viewAll': 'View all',
  'dashboard.businessTitle': 'Business workspace',
  'dashboard.businessBody': 'Team, shared folders and permissions.',
  'dashboard.businessAction': 'Open team',
  'dashboard.personalTitle': 'Personal workspace',
  'dashboard.personalBody': 'Your photos, your albums and everything you starred.',
  'dashboard.personalAction': 'Open photos',

  'dashboard.storageBody': 'What this workspace is using right now.',
  'dashboard.favoritesBody': 'The files you marked to find them fast.',
  'dashboard.startTitle': 'Your space is empty. Let’s start.',
  'dashboard.startBody': 'Three steps and it is up and running.',
  'dashboard.startUpload': 'Upload your files',
  'dashboard.startUploadBody': 'Drag them onto the window or pick them from your computer.',
  'dashboard.startOrganize': 'Sort them into folders',
  'dashboard.startOrganizeBody': 'By client, by project, or however you work.',
  'dashboard.startShare': 'Share what you choose',
  'dashboard.startShareBody': 'With a password, an expiry date and a download limit.',
  // --- The mobile app (phase 19) ---------------------------------------------
  'mobile.title': 'The app on your phone',
  'mobile.body': 'Upload photos and documents from wherever you are, without opening a computer.',
  'mobile.locked': 'Your plan does not include the mobile app.',
  'mobile.lockedWithPlan': 'The mobile app comes with {plan}, at {price} a month.',
  'mobile.upgrade': 'Change plan',
  'mobile.alreadyInstalled': "You already have it installed \u2014 you're using it right now.",
  'mobile.androidDownload': 'Download for Android',
  'mobile.androidMeta': 'Version {version} \u00b7 {size}',
  'mobile.androidWarnUnknownSources':
    'When you install it, Android will ask permission to install from this source. That is expected: the app does not come from the Play Store.',
  'mobile.androidWarnUpdates':
    'It does not update itself. The app will tell you when a new version is out.',
  'mobile.androidSoon': 'The Android app is being prepared. We will tell you right here.',
  'mobile.androidWebApp':
    'In the meantime you can install this site as an app from the Chrome menu: \u201cInstall app\u201d.',
  'mobile.iosAppStore': 'View on the App Store',
  'mobile.iosIntro':
    'On iPhone and iPad you install it from Safari, in three steps. It gets its own icon on the home screen and opens full screen.',
  'mobile.iosStep1': 'Tap the share button, at the bottom centre.',
  'mobile.iosStep2': 'Scroll down and choose \u201cAdd to Home Screen\u201d.',
  'mobile.iosStep3': 'Confirm with \u201cAdd\u201d, at the top right.',
  'mobile.iosOpenInSafari':
    'Open this page in Safari to install it: the other iPhone browsers do not offer that option.',
  'mobile.iosNote': 'We are preparing the App Store listing. Until then, this is the way in.',
  'mobile.qrLabel': 'QR code with the dashboard address',
  'mobile.desktopBody': 'Scan the code with your phone camera to open the dashboard there.',
  'mobile.desktopHint':
    'From the phone you will see how to install it, whether it is Android or iPhone.',

  'storage.used': 'Storage used',
  'storage.remaining': '{amount} remaining',
  'storage.full': 'Storage full',
  'storage.upgrade': 'Upgrade Plan',

  'files.title': 'My Files',
  'files.home': 'Home',
  'files.empty': 'Your cloud is empty.',
  'files.emptyBody': 'Upload your first file to get started.',
  'files.uploadFirst': 'Upload your first file',
  'files.listView': 'List view',
  'files.gridView': 'Grid view',
  'files.moveToTrash': 'Move to trash?',
  'files.moveToTrashBody': '{name} will go to the trash. You can restore it from there.',
  'files.count': '{count, plural, =0 {No files} one {1 file} other {{count} files}}',
  'files.downloadZip': 'Download as ZIP',

  'photos.title': 'Photos',
  'photos.empty': 'Nothing here yet.',
  'photos.emptyBody': 'Upload photos and videos and they will show up here automatically.',
  'photos.albums': 'Albums',
  'photos.selected': '{count, plural, one {1 selected} other {{count} selected}}',

  'favorites.title': 'Favorites',
  'favorites.empty': 'No favorites yet.',
  'favorites.emptyBody': 'Mark a file as favorite and it will appear here.',
  'favorites.remove': 'Remove',

  'trash.title': 'Trash',
  'trash.empty': 'Your trash is empty.',
  'trash.retention':
    'Items are deleted permanently {days, plural, one {1 day} other {{days} days}} after being moved here.',
  'trash.restore': 'Restore',
  'trash.deleteForever': 'Delete forever',
  'trash.confirmTitle': 'Delete permanently?',
  'trash.confirmBody':
    '{name} will be deleted from storage and cannot be recovered. This also frees the space it uses.',

  'shares.title': 'Shared Links',
  'shares.subtitle': 'Every link you created and how it is being used.',
  'shares.copyWarning':
    'Links can only be copied at the moment you create them: we store a hash of the token, not the token itself. Create a new link if you lost the URL.',
  'shares.empty': "You haven't shared anything yet.",
  'shares.emptyBody': 'Create a link from any file to share it.',
  'shares.file': 'File',
  'shares.folder': 'Folder',
  'shares.created': 'Created',
  'shares.expiration': 'Expiration',
  'shares.never': 'Never',
  'shares.downloads': 'Downloads',
  'shares.status': 'Status',
  'shares.disable': 'Disable',
  'shares.linkCopied': 'Link copied',
  'shares.shareFile': 'Share file',
  'shares.passwordOptional': 'Password (optional)',
  'shares.passwordHint': 'Leave empty for anyone with the link.',
  'shares.permissions': 'Permissions',
  'shares.preview': 'Preview',
  'shares.createLink': 'Create link',
  'shares.createdBody':
    'Anyone with this link can open the file. Copy it now — we store only a hash of the token, so this URL cannot be shown again.',

  'activity.title': 'Activity',
  'activity.empty': 'No activity yet.',
  'activity.upgradeTitle': 'Upgrade to unlock this feature',
  'activity.upgradeBody': 'Activity history is available on Plus and Business plans.',
  'activity.viewPlans': 'View Plans',

  'settings.title': 'Settings',
  'settings.profile': 'Profile',
  'settings.security': 'Security',
  'settings.storage': 'Storage',
  'settings.subscription': 'Subscription',
  'settings.firstName': 'First name',
  'settings.lastName': 'Last name',
  'settings.emailVerified': 'Verified.',
  'settings.emailUnverified': 'Unverified',
  'settings.profileBody': 'How you appear across 29100 Cloud.',
  'settings.emailVerifyPending':
    'Not verified yet. Changing the email requires verification, which needs a mail provider.',
  'settings.changePasswordBody': 'Closes every other open session.',
  'settings.sessionsBody': 'Where your account is currently signed in.',
  'settings.passwordChangeFailed': 'Could not change the password',
  'common.loadingPlan': 'Loading plan…',
  'settings.profileUpdated': 'Profile updated',
  'settings.changePassword': 'Change password',
  'settings.currentPassword': 'Current password',
  'settings.newPassword': 'New password',
  'settings.activeSessions': 'Active sessions',
  'settings.thisDevice': 'This device',
  'settings.logoutAll': 'Logout all devices',
  'settings.mfa': 'Two-factor authentication',
  'settings.currentPlan': 'Current plan',
  'settings.changePlan': 'Change plan',
  'settings.languageHint': 'Applies across the app and is remembered on your devices.',

  'pricing.title': 'Plans that grow with you',
  'pricing.subtitle': 'Start free. Upgrade when you need more room.',
  'pricing.personal': 'Personal',
  'pricing.business': 'Business',
  'pricing.months': '{count, plural, one {1 month} other {{count} months}}',
  'pricing.termHint':
    '{free, plural, one {One month free for paying up front.} other {{free} months free for paying up front.}}',
  'pricing.billedUpfront':
    '{count, plural, one {Billed {total} in one go} other {Billed {total} in one go for {count} months}}',
  'pricing.billingPeriod': 'Billing period',
  'pricing.monthly': 'Monthly',
  'pricing.yearly': 'Yearly',
  'pricing.yearlyHint': 'Pay for the year up front and two months are free.',
  'pricing.billedYearly': 'Billed {total} once a year',
  'pricing.currentPlan': 'Current plan',
  'pricing.mostPopular': 'Most popular',
  'pricing.perMonth': '/month',
  'pricing.storage': 'Storage',
  'pricing.users': 'Users',
  'pricing.maxFileSize': 'Max file size',
  'pricing.included': 'Included',
  'pricing.requestPlan': 'Request plan',
  'pricing.requestSent': 'Request sent. We will send you the payment link shortly.',
  'pricing.manualNote':
    'Paid plans are activated manually: we send you a payment link, you pay, and we activate the plan once the payment is confirmed. You will see every step in your dashboard.',
  'pricing.loadFailed': 'We could not load the plans.',

  'upload.optimize': 'Shrink photos before uploading',
  'upload.optimizeHint':
    'Only affects large JPEG and WebP photos; they look the same and take up far less. No other file is touched.',
  'upload.optimizeSaved': 'You saved {amount} of your space.',
  'upload.title': 'Upload files',
  'upload.subtitle': 'Files go straight to storage; they never pass through our servers.',
  'upload.dropHere': 'Drag & drop files here',
  'upload.chooseFiles': 'Choose files',
  'upload.clearCompleted': 'Clear completed',
  'upload.retryFailed':
    '{count, plural, one {Retry the one that failed} other {Retry the {count} that failed}}',
  'upload.uploading': 'Uploading…',
  'upload.statusQueued': 'Queued',
  'upload.statusUploading': 'Uploading',
  'upload.statusProcessing': 'Processing',
  'upload.statusCompleted': 'Completed',
  'upload.statusFailed': 'Failed',
  'upload.errorNotStarted':
    'The upload never started. It is not your connection: storage did not accept the request. If it happens with every file, let us know.',
  'upload.errorInterrupted': 'The connection dropped mid-upload. Please try again.',
  'upload.errorRejected': 'Storage rejected the file (code {status}). Please try again.',
  'upload.errorCancelled': 'Cancelled',
  'upload.errorGeneric': 'Could not upload',

  'error.notFound': 'Page not found',
  'error.notFoundBody': 'The page you are looking for does not exist or was moved.',
  'error.forbidden': "You don't have permission",
  'error.forbiddenBody':
    "You don't have permission to perform this action. Ask an administrator of this workspace.",
  'error.serverError': 'Something went wrong',
  'error.serverErrorBody': 'We could not complete the request. Try again in a moment.',
  'error.goHome': 'Go home',

  'auth.registerTitle': 'Create your account',
  'auth.firstName': 'First name',
  'auth.lastName': 'Last name',
  'auth.creating': 'Creating account…',
  'auth.createAccount': 'Create account',
  'auth.haveAccount': 'Already have an account?',
  'auth.registerFailed': 'Could not complete the registration',
  'auth.passwordHint': 'At least 10 characters, with at least one letter and one number.',
  'auth.accountType': 'What kind of account do you want?',
  'auth.accountTypeHint': 'You can open the other one later, without registering again.',
  'auth.accountTypePersonal': '29100 Cloud Personal',
  'auth.accountTypeBusiness': '29100 Cloud Business',
  'auth.accountTypePersonalNote': 'Your personal space, for you and your family.',
  'auth.accountTypeBusinessNote': 'A company space with its organization, team and shared folders.',
  'auth.organizationName': 'Organization name',
  'auth.organizationNameHint': 'The name your teammates will see when you invite them.',
  'auth.organizationNamePlaceholder': 'Pérez Law Firm',
  'auth.personalUseTitle': 'What will you use 29100 Cloud for?',
  'auth.businessUseTitle': 'What does your organization do?',
  'auth.signInLink': 'Sign in',
  'profile.allSectors': 'All',
  'profile.sector': 'Sector',
  'profile.searchLabel': 'Search profile',
  'profile.searchPlaceholder': 'Search your profession or sector…',
  'profile.groupLabel': 'Usage profile',
  'profile.noMatches':
    'We found no profile for “{query}”. You can pick a general one and adjust it later.',
  'profile.moreQuestions': 'Tell us a bit more',
  'profile.selectPlaceholder': 'Select…',
  'profile.templateTitle': 'We will create this {folders} structure',
  'profile.tailoredHint':
    'We tailor the folders and options to your case. You can change it later.',
  'onboarding.title': 'What will you use your space for?',
  'onboarding.subtitle': 'We tailor folders, wording and features to your line of work.',
  'onboarding.continue': 'Continue',
  'onboarding.creating': 'Creating space…',
  'onboarding.workspaceName': 'Workspace name',
  'onboarding.failed': 'Could not create the workspace',
  'onboarding.allowance': 'Company spaces: {used} of {limit}.',
  'onboarding.allowanceRemaining': 'You can open {remaining} more on your current plan.',
  'onboarding.allowanceExhausted':
    'Your plan ({plan}) allows {limit} company space(s) and you already have them open. Upgrade to open another one.',
  'onboarding.allowanceUpgrade': 'See plans',
  'files.uploadFiles': 'Upload files',
  'files.newFolder': 'New folder',
  'files.folderName': 'Folder name',
  'files.folderCreated': 'Folder created',
  'files.folderFailed': 'Could not create the folder',
  'files.renamePrompt': 'New name',
  'files.renamed': 'File renamed',

  'files.move': 'Move',
  'files.moveTitle': 'Move “{name}”',
  'files.moved': '“{name}” was moved.',

  'folders.destination': 'Destination folder',
  'folders.destinationHint': 'Any folder works, at any level of the tree.',
  'folders.root': 'Workspace root',
  'folders.pickOne': 'Pick a folder…',
  'folders.none': 'No folders yet',
  'files.renameFailed': 'Could not rename',
  'files.favorited': 'Added to favorites',
  'files.favoriteFailed': 'Could not update the favorite',
  'files.downloadFailed': 'Could not prepare the download',
  'files.movedToTrash': 'Moved to trash',
  'files.trashFailed': 'Could not delete the file',
  'files.actionsFor': 'Actions for {name}',
  'files.breadcrumb': 'Breadcrumb',
  'photos.uploadPhotos': 'Upload photos',
  'photos.clearSelection': 'Clear ({count})',
  'photos.deleted': '{count, plural, one {1 moved to trash} other {{count} moved to trash}}',
  'photos.albumsPending': 'Vacation, Family, Work, Events — requires {endpoint}.',
  'photos.select': 'Select {name}',
  'photos.loadMore': 'Load more',
  'photos.countPartial':
    '{count, plural, one {1 item loaded} other {{count} items loaded}} · more to come',
  'trash.restored': 'File restored',
  'trash.deletedForever': 'Deleted permanently',
  'trash.deleteFailed': 'Could not delete the file',
  'trash.purges': 'Purges {date}',
  'shares.deleteTitle': 'Delete this link?',
  'shares.deleteBody':
    'Anyone holding the URL will stop being able to open it. The file itself is not affected.',
  'shares.deleted': 'Link deleted',
  'shares.disabled': 'Link disabled',
  'shares.linkCreated': 'Link created',
  'shares.createFailed': 'Could not create the link',
  'shares.expNever': 'Never',
  'shares.exp1Day': '1 day',
  'shares.exp7Days': '7 days',
  'shares.exp30Days': '30 days',
  'shares.exp90Days': '90 days',
  'shares.unlimited': 'Unlimited',
  'preview.cannotPreview':
    'This format cannot be previewed in the browser. Download it to open it with the right application.',
  'preview.unknownType': 'Unknown type',
  'admin.title': 'Platform panel',
  'admin.overview': 'Overview',
  'admin.plans': 'Plans',
  'admin.plansBody':
    'The catalogue exactly as it sits in the database — the same figures a customer sees on the pricing page.',
  'admin.plansCatalog': 'Active plans',
  'admin.plansPrice': 'Price',
  'admin.plansSpaces': 'Spaces',
  'admin.plansFree': 'Free',
  'admin.plansReadOnly':
    'Read-only. Changing a plan affects every customer on it at once, so it is done in SQL against "plans" and "plan_features".',
  'admin.badge': 'Platform',
  'admin.exitToMySpace': 'Back to my space',
  'admin.notOperator':
    'This account is not a platform operator. The permission is granted in the database.',
  'admin.pending':
    '{count, plural, =0 {no pending requests} one {1 request to handle} other {{count} requests to handle}}',
  'admin.environment': 'Environment {name}',
  'admin.users': 'Users',
  'admin.workspaces': 'Workspaces',
  'admin.organizations': 'Organizations',
  'admin.storage': 'Storage',
  'admin.margins': 'Margins',
  'admin.marginsBody':
    'What each customer costs to serve against what they pay. Loss-making ones first.',
  'admin.marginsEmpty': 'No customer is near their break-even point.',
  'admin.marginsLosing': 'Cost more than they pay',
  'admin.marginsLoss': 'Lost per month',
  'admin.marginsWatch': 'Close to the limit',
  'admin.marginsPays': 'Pays',
  'admin.marginsCosts': 'Costs',
  'admin.marginsMargin': 'Margin',
  'admin.marginsUsed': 'Quota used',
  'admin.marginsBreakeven': 'Break-even',
  'admin.marginsHint':
    'Cost comes from stored bytes at ${price} per GB-month, not from the quota sold: R2 charges for what is there.',
  'admin.marginsTrend': 'Usage trend',
  'admin.marginsTrendBody':
    'How much of the capacity sold is actually taken, day by day. This is what shows whether utilisation is rising.',
  'admin.marginsTrendEmpty':
    'No history yet. A snapshot is stored every night; the trend shows up with the second one.',
  'admin.marginsTrendDelta': '{delta} over {days} days',
  'admin.marginsTrendToday': 'Taken today',
  'admin.marginsTrendMargin': 'Margin today',
  'admin.filesCount': 'Files',
  'admin.byPlan': 'Customers by plan',
  'admin.requestsOpen': 'Unresolved',
  'admin.planRequests': 'Plan requests',
  'admin.refresh': 'Refresh',
  'admin.noRequests': 'No pending requests.',
  'admin.loadFailed': 'Could not load the panel',
  'admin.actionFailed': 'Could not complete the action',
  'planRequest.inProgress': 'request in progress',
  'planRequest.requested':
    'We received your request. We will send you the payment link once we review it; the notice will appear right here.',
  'planRequest.awaiting': 'Pay through {method} and let us know so we can verify it.',
  'planRequest.goToPayment': 'Go to payment',
  'planRequest.reference': 'Payment reference (optional)',
  'planRequest.referenceHint': 'Operation or transfer number',
  'planRequest.declarePaid': "I've paid",
  'planRequest.amount': 'To pay',
  'planRequest.term': 'Term',
  'planRequest.perMonth': '{amount} per month',
  'planRequest.savings': 'You save {amount}',
  'planRequest.goToPaymentAmount': 'Go to pay {amount}',
  'planRequest.checking':
    'Thanks. We are verifying the payment; once confirmed, your plan activates on its own and we let you know.',
  'privacy.title': 'Privacy',
  'privacy.exportTitle': 'Export my data',
  'privacy.exportBody':
    'Download a copy of your profile, workspaces, file inventory, links and activity. The contents of each file are downloaded from the app.',
  'privacy.exportAction': 'Download my data',
  'privacy.exportDone': 'Download ready',
  'privacy.deleteTitle': 'Delete my account',
  'privacy.deleteBody':
    'Your files and personal data are deleted. Billing records are kept without any link to you, as accounting requires.',
  'privacy.deleteAction': 'Request deletion',
  'privacy.deleteConfirmTitle': 'Delete your account?',
  'privacy.deleteConfirmBody':
    'You have {days, plural, one {1 day} other {{days} days}} to change your mind: during that time the account keeps working and you can cancel from here. After that, your files are deleted and cannot be recovered.',
  'privacy.deleteConfirm': 'Yes, delete my account',
  'privacy.pendingTitle': 'Deletion scheduled',
  'privacy.pendingBody':
    'Your account and files will be deleted on {date}. Everything keeps working until then.',
  'privacy.cancelDeletion': 'Keep my account',
  'privacy.cancelled': 'Deletion cancelled',
  'privacy.requested': 'Deletion scheduled',
  'notifications.markAllRead': 'Mark all as read',
  'notifications.empty': "You're all caught up.",
  'notifications.new': 'New',
  'notifications.unreadCount': '{count, plural, one {1 unread} other {{count} unread}}',
  'workspace.switch': 'Switch workspace',
  'workspace.active': 'Active',
  'workspace.new': 'New workspace',
  'workspace.exploreBusiness': 'Explore Business',
  'upload.announceProgress': '{name}, {percent}%',
  'upload.announceCompleted': '{name} uploaded successfully',
  'upload.announceFailed': '{name} failed to upload',
  'mfa.title': 'Two-factor authentication',
  'mfa.intro':
    'Add a one-time code on top of your password. If someone gets your password, they still cannot get in.',
  'mfa.statusOn': 'On',
  'mfa.statusOff': 'Off',
  'mfa.enable': 'Turn on',
  'mfa.disable': 'Turn off',
  'mfa.step1': 'Scan this code with your authenticator app',
  'mfa.step1Help':
    'Any app works: Google Authenticator, Authy, 1Password, Microsoft Authenticator.',
  'mfa.qrLabel': 'QR code to set up two-factor authentication',
  'mfa.manualEntry': "Can't scan? Type this key instead:",
  'mfa.step2': 'Enter the six-digit code shown by the app',
  'mfa.code': 'Code',
  'mfa.codePlaceholder': '000000',
  'mfa.confirm': 'Confirm and turn on',
  'mfa.cancel': 'Cancel',
  'mfa.enabled': 'Two-factor authentication is on',
  'mfa.disabled': 'Two-factor authentication is off',
  'mfa.recoveryTitle': 'Save these recovery codes',
  'mfa.recoveryBody':
    'Each one works once if you lose your phone. **They cannot be shown again**: we store only their fingerprint, just like passwords. Copy them now.',
  'mfa.recoveryDone': "I've saved them",
  'mfa.recoveryRemaining':
    '{count, plural, =0 {No recovery codes left} one {1 recovery code left} other {{count} recovery codes left}}',
  'mfa.regenerate': 'Generate new codes',
  'mfa.regenerateHelp': 'The previous ones will stop working.',
  'mfa.disableTitle': 'Turn off two-factor authentication',
  'mfa.disableBody':
    'We ask for your password and a code: with the session alone, whoever stole it could remove the very protection that stops them.',
  'mfa.password': 'Your password',
  'mfa.challengeTitle': 'Two-factor authentication',
  'mfa.challengeBody': 'Enter the code from your authenticator app.',
  'mfa.challengeRecovery': 'You can also use one of your recovery codes.',
  'mfa.verify': 'Verify',
  'mfa.copySecret': 'Copy key',
  'mfa.copied': 'Copied',
  'password.problems': 'Please choose a different password:',
  'password.TOO_SHORT': 'It must be at least 10 characters.',
  'password.TOO_LONG': 'It is too long.',
  'password.MISSING_LETTER': 'It must include a letter.',
  'password.MISSING_DIGIT': 'It must include a number, or be at least 16 characters long.',
  'password.COMMON': 'It is one of the most used passwords in the world.',
  'password.SEQUENTIAL': 'It contains a predictable run, like "1234" or "abcd".',
  'password.KEYBOARD_PATTERN': 'It contains a keyboard pattern, like "qwerty".',
  'password.REPEATED': 'It repeats the same chunk over and over.',
  'password.LOW_VARIETY': 'It uses very few different characters.',
  'password.CONTAINS_EMAIL': 'It contains your email address.',
  'password.CONTAINS_NAME': 'It contains your name.',
  'password.CONTAINS_APP_NAME': 'It contains the name of the service.',

  'team.title': 'Team',
  'team.subtitle': 'Who can get in, and what they can do.',
  'team.invite': 'Invite',
  'team.inviteTitle': 'Invite someone',
  'team.inviteBody':
    'If they already have an account they get a notice and decide. If they do not, you can create it.',
  'team.email': "Person's email",
  'team.firstName': 'First name (optional)',
  'team.lastName': 'Last name (optional)',
  'team.createAccountTitle': 'That email has no account',
  'team.createAccountBody': 'Nobody has used {email} to sign up for 29100 Cloud.',
  'team.createAccountHelp':
    'You can open the account now and add them to the team. We will give you a temporary password to pass along; the email is not checked, so look it over first.',
  'team.createAccount': 'Create the account',
  'team.accountCreated': 'Account created',
  'team.accountCreatedBody': 'They are on the team now. Pass these details to {email}.',
  'team.accountAdded': 'Account created and added to the team',
  'team.temporaryPassword': 'Temporary password',
  'team.temporaryPasswordOnce':
    'Copy it now: it is not stored anywhere and will not be shown again. If you lose it, they can recover it from "I forgot my password". They should change it once they are in.',
  'team.role': 'Role',
  'team.invited': 'Invitation sent',
  'team.pending': 'Waiting to accept',
  'team.member': 'Member',
  'team.you': 'You',
  'team.changeRole': 'Change role',
  'team.remove': 'Remove from team',
  'team.removeTitle': 'Remove {name} from the team?',
  'team.removeBody': 'They lose access to the organization right away. You can invite them again.',
  'team.removed': 'Removed from the team',
  'team.roleChanged': 'Role updated',
  'team.empty': "It's just you so far.",
  'team.emptyBody': 'Invite your team to work on the same folders.',
  'team.count': '{count, plural, one {1 person} other {{count} people}}',
  'team.seats': '{used} of {limit} seats on the {plan} plan',
  'team.seatsFull': 'No seats left',
  'team.seatsFullBody':
    'Your {plan} plan allows {limit, plural, one {1 person} other {{limit} people}}, and they are all taken. Upgrade the plan to keep adding people to the team.',
  'team.seatsUpgrade': 'Upgrade the plan',
  'team.noOrganization': 'This section belongs to 29100 Cloud Business.',
  'team.noOrganizationBody': 'Switch to an organization space, or create one.',
  'role.OWNER': 'Owner',
  'role.ADMIN': 'Admin',
  'role.MEMBER': 'Member',
  'role.VIEWER': 'Viewer',
  'role.OWNER.help': 'Full control, including billing.',
  'role.ADMIN.help': 'Manages people, folders and permissions.',
  'role.MEMBER.help': 'Uploads, edits and shares where granted.',
  'role.VIEWER.help': 'Can only read and download.',
  'invitation.title': "You've been invited",
  'invitation.body': '{who} invited you to {organization} as {role}.',
  'invitation.bodyNoOne': 'You were invited to {organization} as {role}.',
  'invitation.accept': 'Join',
  'invitation.decline': 'Decline',
  'invitation.accepted': "You're now part of {organization}",
  'invitation.declined': 'Invitation declined',

  'admin.organizationsBody': 'Every company on the platform, its plan and its usage.',
  'admin.members': 'People',
  'admin.pendingInvites': '{count, plural, =0 {} one {1 pending} other {{count} pending}}',
  'admin.owner': 'Owner',
  'admin.orgState': 'State',
  'admin.orgSpaces': 'Spaces',
  'admin.orgActive': 'Active',
  'admin.orgSuspended': 'Suspended',
  'admin.orgDeletedState': 'Marked as deleted',
  'admin.orgOrphan': 'No space',
  'admin.orgOrphanHint':
    'It has no space at all: nothing to open and nothing being used. Usually what a sign-up that never finished leaves behind.',
  'admin.orgDeleting': 'Deleted on {date}',
  'admin.orgOwnerMismatch': 'The space owner is {email}',
  'admin.orgOwnerMismatchHint':
    'The organization and its space have different owners. They are the same person when it is created, so this comes from a change made straight in the database.',
  'admin.orgOpen': 'Open customer',
  'admin.storageBody': 'How much is stored, under which plan, and who uses the most.',
  'admin.topPersonal': 'Personal accounts using the most',
  'admin.topBusiness': 'Companies using the most',
  'admin.spaces': 'Spaces',
  'admin.files': 'Files',
  'admin.audit': 'Audit trail',
  'admin.auditBody': 'Everything that happened on the platform, by whom and when.',
  'admin.action': 'Action',
  'admin.actor': 'Who',
  'admin.when': 'When',
  'admin.resource': 'On what',
  'admin.allActions': 'All actions',
  'admin.anonymized': 'Closed account',
  'admin.auditEmpty': 'No records match that filter.',
  'admin.more': 'Show more',
  'admin.plansPending':
    'Editing plans is done in SQL, deliberately: a mistyped plan affects every customer on it.',

  'sharing.title': 'Who has access',
  'sharing.restricted': 'Restricted access',
  'sharing.open': 'The whole team can see it',
  'sharing.openBody': 'Anyone in the organization can open this folder.',
  'sharing.restrictedBody':
    'Only the people listed can get in. The rest of the team cannot see it or know it exists.',
  'sharing.share': 'Give access',
  'sharing.shareTitle': 'Give access to this folder',
  'sharing.shareBody':
    'Giving someone access hides the folder from the rest of the team. They must already be on the team.',
  'sharing.email': "Person's email",
  'sharing.permissions': 'What they can do',
  'sharing.granted': 'Access granted',
  'sharing.revoke': 'Remove access',
  'sharing.revoked': 'Access removed',
  'sharing.openUp': 'Open it to the whole team again',
  'sharing.openUpTitle': 'Open this folder to the whole team?',
  'sharing.openUpBody':
    'Anyone in the organization will see it again, and the granted accesses will be removed.',
  'sharing.opened': 'The folder belongs to the whole team again',
  'sharing.nobody': "You haven't given access to anyone yet.",
  'sharing.you': 'You, for administering the organization',
  'perm.VIEW': 'View',
  'perm.DOWNLOAD': 'Download',
  'perm.UPLOAD': 'Upload',
  'perm.CREATE_FOLDER': 'Create folders',
  'perm.EDIT': 'Edit',
  'perm.DELETE': 'Delete',
  'perm.SHARE': 'Share',
  'sharedFolders.title': 'Shared Folders',
  'sharedFolders.subtitle': 'Team folders you have been given access to.',
  'sharedFolders.empty': 'Nobody has given you access to a folder.',
  'sharedFolders.emptyBody': 'When someone restricts a folder and includes you, it shows up here.',
  'sharedFolders.open': 'Open',

  'subscription.canceling': 'Cancelling',
  'subscription.paidUntil': 'Paid until',
  'subscription.paidOn': 'Payment date',
  'subscription.term': 'Contracted term',
  'subscription.trial': 'On trial',
  'subscription.trialUntil': 'Trial until',
  'subscription.daysLeft': '{count, plural, one {1 day left} other {{count} days left}}',
  'subscription.expiredAlready': 'expired',
  'subscription.renewalPolicy':
    'If it is not renewed, the subscription lapses on {date}. From then on the space becomes read-only after {suspend} days and is deleted after {remove}. We warn you before every step.',
  'subscription.renew': 'Renew plan',
  'subscription.expiringTitle': 'Your plan expires soon',
  'subscription.expiringBody':
    'Your plan expires on {date}: {days, plural, one {1 day left} other {{days} days left}}. Renew it and nothing is interrupted.',
  'subscription.lapsedTitle': 'Your plan has expired',
  'subscription.lapsedBody':
    'Nothing has changed yet. If payment does not arrive before {date}, the space falls into arrears and becomes read-only {days} days later.',
  'subscription.unpaidTitle': 'Your space has a payment pending',
  'subscription.unpaidGrace':
    'Everything still works. If payment does not arrive, on {suspend} the space becomes read-only, on {block} you lose access and on {delete} it is deleted for good.',
  'subscription.unpaidSuspended':
    'The space is read-only: you can sign in, browse and download, but not upload. On {block} you lose access and on {delete} it is deleted for good. Download whatever you need before {block}.',
  'subscription.unpaidBlocked':
    'The space is blocked and will be deleted for good on {delete}. To get it back it must be paid before that date: afterwards there is no copy to restore.',
  'subscription.unpaidStepSuspend': 'Read-only',
  'subscription.unpaidStepBlock': 'No access',
  'subscription.unpaidStepDelete': 'Permanent deletion',
  'subscription.unpaidIrreversible': 'cannot be undone',
  'receipts.title': 'My receipts',
  'receipts.body':
    'What you contracted and what you paid. Each line says which period it covers and the reference the payment came in with.',
  'receipts.empty': 'No receipts yet. They appear once a paid plan is activated.',
  'receipts.number': 'No.',
  'receipts.plan': 'Plan',
  'receipts.period': 'Period',
  'receipts.term': 'Term',
  'receipts.amount': 'Amount',
  'receipts.method': 'Method',
  'receipts.reference': 'Reference',
  'receipts.paid': 'Paid',
  'receipts.unpaid': 'Outstanding',
  'receipts.freeMonths': '{count, plural, one {1 month free} other {{count} months free}}',
  'receipts.months': '{count, plural, one {1 month} other {{count} months}}',
  'admin.receipts': 'Collections',
  'admin.receiptsBody':
    'Everything invoiced and collected, with who signed off on each payment. This is what you look at to close a dispute.',
  'admin.receiptsEmpty': 'Nothing collected yet.',
  'admin.receiptsCollected': 'Collected in {days} days',
  'admin.receiptsCount': 'Receipts issued',
  'admin.receiptsRecordedBy': 'Confirmed by',
  'subscription.cancelTitle': 'Cancel subscription',
  'subscription.cancelBody':
    'You keep everything until the end of the period you already paid for. After that the workspace goes back to the free plan, and if your files no longer fit you get {days} days to free up space before we move the excess to the trash.',
  'subscription.cancelAction': 'Cancel subscription',
  'subscription.confirmTitle': 'Cancel your subscription?',
  'subscription.confirmBody':
    'Your plan stays active until {date}. After that the workspace goes back to the free plan, and if your files no longer fit you get {days} days to free up space. You can undo this at any time before {date}.',
  'subscription.canceled': 'Cancelled. Your plan stays active until the end of the period.',
  'subscription.pendingTitle': 'Cancellation scheduled',
  'subscription.pendingBody':
    'Your plan stays active until {date}. Nothing changes before then, and you can still undo it.',
  'subscription.pendingBodyNoDate':
    'Your plan will go back to the free one when the current period ends.',
  'subscription.reactivate': 'Keep my subscription',
  'subscription.back': 'Cancellation undone. Your subscription continues.',
  'subscription.overQuotaTitle': 'Your files no longer fit your plan',
  'subscription.overQuotaBody':
    'You can read and download everything, but not upload. You have {days} days: on {date} we will move the largest files to the trash until you are back within the limit. Nothing is deleted for good — the trash keeps its own retention.',
  'subscription.overQuotaFree': 'Free up space',

  'reports.title': 'Reports',
  'reports.subtitle': 'What your team stores, how it grows and what it does.',
  'reports.restricted': 'Only owners and admins',
  'reports.restrictedBody':
    'These reports say how much each person stores. That is information about your colleagues, so it stays with whoever runs the organization.',
  'reports.totalStored': 'Stored by the team',
  'reports.totalFiles': 'Files',
  'reports.byMember': 'Who stores what',
  'reports.byMemberBody': 'Files in the trash are not counted against anyone.',
  'reports.empty': 'Nothing stored yet.',
  'reports.formerMember': 'Account no longer active',
  'reports.exportCsv': 'Export CSV',
  'reports.growth': 'Growth',
  'reports.growthBody': 'How much came in each month, and the running total.',
  'reports.growthCaveat':
    'This is the upload date of what exists today, not a historical snapshot: anything deleted is not counted. It answers when you will need a bigger plan, which is what it is for.',
  'reports.range': 'Range',
  'reports.months': '{count} months',
  'reports.month': 'Month',
  'reports.added': 'Added',
  'reports.cumulative': 'Running total',
  'reports.activity': 'Team activity',
  'reports.activityBody': 'What was uploaded, shared and deleted.',
  'reports.noActivity': 'No activity recorded.',

  'tenants.title': 'Customers',
  'tenants.subtitle': 'Who pays, how much they consume, who is leaving.',
  'tenants.mrr': 'Monthly recurring',
  'tenants.paid': 'On a paid plan',
  'tenants.canceling': 'Cancelling',
  'tenants.suspendedCount': 'Suspended',
  'tenants.search': 'Workspace or owner email',
  'tenants.state': 'State',
  'tenants.stateAll': 'All',
  'tenants.state_paid': 'Paid',
  'tenants.state_free': 'Free',
  'tenants.state_canceling': 'Cancelling',
  'tenants.state_over_quota': 'Over quota',
  'tenants.state_suspended': 'Suspended',
  'tenants.state_unpaid': 'Unpaid',
  'tenants.expiringCount': 'Expiring soon',
  'tenants.expiringHint': 'They need to be charged before they expire.',
  'tenants.unpaidCount': 'Unpaid',
  'tenants.unpaidHint': 'With the clock running towards deletion.',
  'tenants.paidUntil': 'Paid until',
  'tenants.periodStart': 'Period start',
  'tenants.billing': 'Billing',
  'tenants.billingHint':
    'Catalogue price: {amount} per month. What was actually charged, with the term discount applied, is on each receipt.',
  'tenants.unpaidTitle': 'This customer stopped paying',
  'tenants.unpaidBody':
    'The clock has been running since {date}. Assigning a paid plan closes it and lifts the suspension right away.',
  'tenants.unpaidHintDetail':
    '{days, plural, =0 {Less than a day} one {1 day} other {{days} days}} until permanent deletion. Afterwards there is no copy to restore.',
  'tenants.sort': 'Sort by',
  'tenants.sortRecent': 'Newest',
  'tenants.sortStorage': 'Storage',
  'tenants.sortName': 'Name',
  'tenants.apply': 'Apply',
  'tenants.empty': 'No customers match that.',
  'tenants.showing': 'Showing {shown} of {total}.',
  'tenants.tenant': 'Workspace',
  'tenants.plan': 'Plan',
  'tenants.usage': 'Storage',
  'tenants.people': 'People',
  'tenants.overQuota': 'Over quota',
  'tenants.suspended': 'Suspended',
  'tenants.suspend': 'Suspend',
  'tenants.reactivate': 'Reactivate',
  'tenants.reactivated': 'Workspace active again.',
  'tenants.suspendedTitle': 'This workspace is read-only',
  'tenants.suspendedBody': 'Suspended on {date}. They can still sign in and download.',
  'tenants.suspendBody':
    'They keep reading and downloading — cutting someone off from their own files over an invoice turns a billing dispute into a data-protection complaint. What they lose is uploading, editing, deleting and sharing.',
  'tenants.reason': 'Reason',
  'tenants.reasonHint': 'Whoever takes the support call will not be whoever suspended it.',
  'tenants.changePlan': 'Change plan',
  'tenants.recalculate': 'Recalculate usage',
  'tenants.recalculated': 'Usage recalculated from the actual files',
  'tenants.changePlanBody':
    'Applies the plan directly, without a request. Use it for a payment that arrived another way or to fix a mistake — it is recorded with your name.',
  'tenants.months': 'Months',
  'tenants.planChanged': 'Plan updated.',
  'tenants.chargeLabel': 'To charge',
  'tenants.listWas': 'was {amount}',
  'tenants.untilDate': 'until {date}',
  'tenants.adjustPeriod': 'Adjust term',
  'tenants.adjustPeriodBody':
    'Move the end date without touching the plan or the start. For extending a trial or applying a negotiated contract.',
  'tenants.currentEnd': 'Current term',
  'tenants.resultingEnd': 'Would run until',
  'tenants.days': 'days',
  'tenants.customDays': 'Other days (negative to shorten)',
  'tenants.untilExact': 'Or until this exact date',
  'tenants.untilExactHint': 'Replaces the days. The term ends at the end of that day.',
  'tenants.periodReasonHint':
    'Required. It is what the customer will read and what records in the audit who granted the time.',
  'tenants.periodAdjusted': 'Term updated.',
  'tenants.shorteningWarning':
    'This date has already passed: the customer enters the countdown towards suspension and, if nobody pays, deletion. Make sure this is what you want.',
  'tenants.trial': 'Trial',
  'tenants.trialLabel': 'Grant as a trial, no charge',
  'tenants.trialHint':
    'Same features as a paid plan, but it does not count as a paying customer nor add to monthly revenue.',
  'tenants.trialCount': 'On trial',
  'tenants.trialHintCard': 'They do not add to monthly revenue.',
  'tenants.state_trial': 'On trial',
  'tenants.state_expiring': 'Expiring soon',
  'tenants.trialUntil': 'Trial until {date}',
  'tenants.since': 'Customer since {date}',
  'tenants.createdHint': 'Data as of {date}.',
  'tenants.state_deleting': 'Scheduled for deletion',
  'tenants.deletingCount': 'Scheduled for deletion',
  'tenants.deletingHint': 'Counting down. It can still be called off.',
  'tenants.delete': 'Delete customer',
  'tenants.deleteTitle': 'Delete “{name}”?',
  'tenants.deleteBodyEmpty':
    'This space holds no files. It will be deleted for good in {days} days, and if anyone signs in before then the deletion calls itself off.',
  'tenants.deleteBodyData':
    'This space holds {files, plural, one {# file} other {# files}} ({size}). It goes read-only —the customer can still download everything— and will be deleted for good in {days} days. After that there is no way back.',
  'tenants.deleteReasonHint':
    'Required when there are files. The customer reads it, and it is what the audit trail keeps once the space is gone.',
  'tenants.deleteConfirm': 'Schedule deletion',
  'tenants.deleteScheduled': 'Deletion scheduled for {date}.',
  'tenants.deletionTitle': 'This space is scheduled for deletion',
  'tenants.deletionBody':
    'It will be deleted for good on {date}, in {days, plural, one {# day} other {# days}}.',
  'tenants.deletionEmptyHint':
    'It was empty when scheduled: if anyone signs in before that date, the deletion calls itself off.',
  'tenants.deletionDataHint':
    'Until then it stays read-only, so the customer can download their data up to the last day.',
  'tenants.deletionRequestedBy': 'Requested by {email} on {date}.',
  'tenants.cancelDeletion': 'Call off deletion',
  'tenants.deletionCanceled': 'Deletion called off. The space stays.',
  'tenants.deletingBadge': 'Deleted on {date}',

  'albums.title': 'Albums',
  'albums.subtitle': 'Collections of photos. A photo can be in several without being copied.',
  'albums.new': 'New album',
  'albums.name': 'Album name',
  'albums.empty': 'No albums yet.',
  'albums.emptyBody': 'Select photos in the gallery and create an album from them.',
  'albums.emptyAlbum': 'This album is empty.',
  'albums.emptyAlbumBody': 'Upload photos or videos here, or add what is already in your gallery.',
  'albums.photoCount': '{count, plural, one {# item} other {# items}}',
  'albums.createFrom': 'Create album',
  'albums.createBody': '{count, plural, one {# item} other {# items}} will go into it.',
  'albums.created': 'Album created with {count, plural, one {# item} other {# items}}.',
  'albums.createdEmpty': 'Album created.',
  'albums.renamed': 'Album renamed.',
  'albums.deleted': 'Album deleted. Your photos are still there.',
  'albums.deleteTitle': 'Delete “{name}”?',
  'albums.deleteBody':
    'The album disappears, the photos stay. Deleting a collection does not delete what it collected.',
  'albums.removeFrom': 'Remove from album',
  'albums.removed': '{count, plural, one {# item} other {# items}} removed from the album.',
  'albums.addPhotos': 'Add photos',
  'albums.addFromGallery': 'Add from gallery',
  'albums.addFromGalleryBody': 'Only what is not in this album yet is listed.',
  'albums.selectToAdd': 'Add {name}',
  'albums.addSelected': 'Add {count, plural, one {# item} other {# items}}',
  'albums.alreadyIn': 'Those were already in the album.',
  'albums.added': '{count, plural, one {# item} other {# items}} added to the album.',
  'albums.uploaded':
    '{count, plural, one {# item} other {# items}} uploaded and added to the album.',
  'albums.allPhotosIn': 'Everything you have is already in this album.',
  'albums.noPhotosYet': 'You have no photos or videos yet.',
  'albums.noPhotosYetBody': 'Upload them from the album and they go straight into it.',
  'albums.addTo': 'Add to an album',
  'albums.addToBody': '{count, plural, one {# item} other {# items}} will go into the album.',
  'albums.pick': 'Album',
  'shares.shareAlbum': 'Share album',
  'photos.deleteConfirm': 'Delete {count, plural, one {# item} other {# items}}?',
  'photos.deleteConfirmBody': 'They go to the trash, where you can still get them back.',
  'settings.avatarChange': 'Change photo',
  'settings.avatarRemove': 'Remove photo',
  'settings.avatarHint': 'JPG, PNG, WebP or GIF, up to 2 MB.',
  'settings.avatarUpdated': 'Profile photo updated.',
  'settings.avatarRemoved': 'Profile photo removed.',
  'publicShare.albumTitle': 'Shared album',
  'publicShare.albumCount': '{count, plural, one {# photo} other {# photos}}',

  'checklists.title': 'Checklists',
  'checklists.subtitle':
    'Which documents each procedure needs. Using a suggested one copies it, and from then on it is yours.',
  'checklists.new': 'New checklist',
  'checklists.name': 'Checklist name',
  'checklists.mine': 'My checklists',
  'checklists.suggested': 'Suggested for your profession',
  'checklists.suggestedBody':
    'They come from the use you chose for this space. You can change them later.',
  'checklists.useTemplate': 'Use this checklist',
  'checklists.alreadyUsed': 'Already used',
  'checklists.itemCount': '{count, plural, one {# item} other {# items}}',
  'checklists.empty': 'You do not have any checklists yet.',
  'checklists.emptyBody': 'Start from a suggested one, or build your own from scratch.',
  'checklists.noItems': 'This checklist has no items yet.',
  'checklists.created': 'Checklist created.',
  'checklists.renamed': 'Checklist renamed.',
  'checklists.deleted': 'Checklist deleted.',
  'checklists.deleteTitle': 'Delete \u201c{name}\u201d?',
  'checklists.deleteBody':
    'The checklist and its items disappear. Your files and folders are untouched.',
  'checklists.copyNotice':
    'This checklist is your own copy: edit it however you like. The original template does not change.',
  'checklists.addItem': 'Add an item',
  'checklists.itemLabel': 'Item name',
  'checklists.itemRemoved': 'Item deleted.',
  'checklists.itemRenamed': 'Item renamed.',
  'checklists.optional': 'optional',
  'checklists.markOptional': 'Mark as optional',
  'checklists.markRequired': 'Mark as required',
  'checklists.moveUp': 'Move \u201c{label}\u201d up',
  'checklists.moveDown': 'Move \u201c{label}\u201d down',

  'requests.title': 'Document requests',
  'requests.subtitle':
    'Ask for a list of documents and share a link. Whoever receives it uploads each one without needing an account.',
  'requests.new': 'New request',
  'requests.empty': 'You have not asked for any documents yet.',
  'requests.emptyBody':
    'Pick a checklist, say which folder the documents land in, and share the link.',
  'requests.progress': '{done} of {total} delivered',
  'requests.progressLabel': 'Progress of \u201c{title}\u201d',
  'requests.usedOfMax': '{used} of {max}',
  'requests.expiresOn': 'until {date}',
  'requests.pending': 'Not delivered yet',
  'requests.view': 'View',
  'requests.openFolder': 'Open the folder',
  'requests.inTrash': 'In the trash',
  'requests.status.open': 'Open',
  'requests.status.completed': 'Completed',
  'requests.status.closed': 'Closed',
  'requests.status.expired': 'Expired',
  'requests.folder': 'Folder the documents land in',
  'requests.folderHint': 'Delivered files show up here, like any other file.',
  'requests.folderPick': 'Choose a folder\u2026',
  'requests.checklist': 'Checklist',
  'requests.checklistHint': 'The documents you ask for come from here.',
  'requests.checklistPick': 'Choose a checklist\u2026',
  'requests.titleField': 'Title',
  'requests.titleHint': 'Left empty, the checklist name is used.',
  'requests.recipient': 'Who it is for',
  'requests.recipientHint': 'Only you see this: it tells several similar requests apart.',
  'requests.message': 'Message for the recipient',
  'requests.newLink': 'Issue a new link',
  'requests.createdNoLink': 'The request was created',
  'requests.createdNoLinkBody':
    '“{title}” is already in the list, but we could not show you its link. Issue one now so you can send it.',
  'requests.issueLink': 'Issue the link',
  'requests.planLimit':
    'Your plan allows {limit, plural, one {# open request} other {# open requests}} at a time. Close one to create another, or change plan.',
  'requests.linkTitle': 'The request link',
  'requests.linkBody':
    'Copy it now: it cannot be shown again. We only store its fingerprint, so if you lose it you have to issue another one and the previous one stops working.',
  'requests.linkField': 'Link',
  'requests.linkCopied': 'Link copied.',
  'requests.linkCopyFailed': 'Could not copy. Select it and copy it by hand.',
  'requests.close': 'Close request',
  'requests.closeTitle': 'Close \u201c{title}\u201d?',
  'requests.closeBody': 'The link will stop accepting documents. What arrived stays.',
  'requests.closed': 'Request closed.',
  'requests.deleteTitle': 'Delete \u201c{title}\u201d?',
  'requests.deleteBody':
    'The request disappears, the documents do not: the ones that arrived stay in their folder.',
  'requests.deleted': 'Request deleted.',

  'apiError.UNAUTHORIZED': 'You need to sign in.',
  'apiError.SESSION_EXPIRED': 'Your session expired. Please sign in again.',
  'apiError.FORBIDDEN': "You don't have permission to perform this action.",
  'apiError.NOT_FOUND': 'We could not find what you are looking for.',
  'apiError.EMAIL_NOT_REGISTERED':
    'That email has no 29100 Cloud account yet. Ask them to sign up, then invite them again.',
  'apiError.MODULE_NOT_IN_WORKSPACE': 'This section belongs to business accounts.',
  'apiError.VALIDATION_ERROR': 'Please check the values you entered.',
  'apiError.STORAGE_LIMIT_EXCEEDED': 'Your storage is full.',
  'apiError.PLAN_LIMIT_EXCEEDED': 'You have reached your plan limit for this.',
  'apiError.FILE_TOO_LARGE': 'The file exceeds the maximum size of your plan.',
  'apiError.CSRF_TOKEN_INVALID': 'Your session expired. Reload the page and try again.',
  'apiError.RATE_LIMITED': 'Too many attempts. Please wait a moment.',
  'apiError.CONFLICT': 'That action conflicts with the current state.',
  'apiError.INVALID_CREDENTIALS': 'Incorrect email or password.',
  'apiError.ACCOUNT_SUSPENDED': 'Your account is suspended.',
  'apiError.NETWORK_ERROR':
    'We could not reach the server. Check your connection; if it keeps happening, let us know.',
  'apiError.DATABASE_UNAVAILABLE':
    'The service is temporarily unavailable. Please try again in a few minutes.',
  'apiError.STORAGE_UNAVAILABLE': 'Storage did not accept the operation. Please try again.',
  'apiError.INTERNAL_ERROR': 'An unexpected error occurred.',
};
